import { Modal, Pressable, View, ViewProps, Platform } from "react-native"
import { useReanimatedKeyboardAnimation } from "react-native-keyboard-controller"
import A, { useAnimatedStyle, withSpring } from "react-native-reanimated"
import { cn } from "@src/utils"
import Icon from "@react-native-vector-icons/material-design-icons"
import { Text, TypographyProps } from "./typography"

interface SheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
}

export function Sheet({ open, onOpenChange, children }: SheetProps) {
  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={() => onOpenChange(false)}>
      <Pressable className="absolute inset-0 bg-black/60" onPress={() => onOpenChange(false)} />
      {children}
    </Modal>
  )
}

export function SheetContent({ className, children, side = "bottom" }: { className?: string; children: React.ReactNode; side?: "bottom" | "right" }) {
  const { height, progress } = useReanimatedKeyboardAnimation()

  const animatedStyle = useAnimatedStyle(() => {
    // Only animate on keyboard for bottom sheet on mobile potentially,
    // but for now simple spring transform for keyboard avoidance if needed
    if (Platform.OS === "ios" || Platform.OS === "android") {
      return {
        transform: [{ translateY: withSpring(height.value * progress.value * 0.3) }],
      }
    }
    return {}
  }, [])

  if (side === "right" && Platform.OS === "web") {
    return <View className={cn("absolute right-0 top-0 bottom-0 h-full w-[350px] bg-white shadow-lg p-6 border-l border-gray-200", className)}>{children}</View>
  }

  return (
    <View className="flex-1 justify-end" style={{ pointerEvents: "box-none" }}>
      <A.View style={animatedStyle} className="w-full">
        <View className={cn("bg-white w-full rounded-t-xl p-6 shadow-xl max-h-[90%]", className)}>
          <View className="w-12 h-1.5 bg-gray-300 rounded-full self-center mb-6" />
          {children}
        </View>
      </A.View>
    </View>
  )
}

export function SheetHeader({ className, ...props }: ViewProps) {
  return <View className={cn("flex flex-col space-y-2 mb-4", className)} {...props} />
}

export function SheetTitle({ className, ...props }: TypographyProps) {
  return <Text variant="h3" className={cn("text-lg font-semibold text-gray-900", className)} {...props} />
}

export function SheetDescription({ className, ...props }: TypographyProps) {
  return <Text variant="muted" className={cn("text-sm text-gray-500", className)} {...props} />
}

export function SheetClose({ onPress }: { onPress: () => void }) {
  return (
    <Pressable onPress={onPress} className="absolute right-4 top-4 opacity-70">
      <Icon name="close" size={24} color="#000" />
    </Pressable>
  )
}
